import { css } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';
import { Link as ReactRouterLink } from 'react-router';

import _ from 'lodash';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import MiniProfileRenderer from 'bundles/classmates/components/MiniProfileRenderer';
import ProfileImageWithRole from 'bundles/classmates/components/ProfileImageWithRole';
import type { NormalizedProfile } from 'bundles/classmates/stores/ClassmatesProfileStore';
import { getCourseRoleString, getCourseRoleValue } from 'bundles/classmates/utils/lib';
import { getProfileLink } from 'bundles/discussions/utils/discussionsUrl';

const profileAreaStyle = css`
  .rc-ProfileImage .c-profile-initials {
    background-color: var(--cds-color-blue-50);
  }

  .c-profile-image-wrapper {
    cursor: pointer;
  }

  .rc-ProfileImage {
    display: inline-block;

    .c-profile-image {
      margin: 0 4px 0 0;
    }
  }
`;

type ProfileAreaProps = {
  courseRole?: string;
  externalUserId: string;
  helperStatus?: string;
  profile?: NormalizedProfile;
  fullName: string;
  profileImageUrl?: string;
  profileLinkId?: string;
};

const ProfileArea = ({
  courseRole,
  externalUserId,
  fullName,
  helperStatus,
  profileImageUrl,
  profile,
  profileLinkId,
}: ProfileAreaProps) => {
  const [showMini, setShowMini] = useState(false);

  const role = getCourseRoleValue(courseRole, helperStatus);

  const roleString = getCourseRoleString(role);
  const hasRole = typeof roleString !== 'undefined' && roleString !== 'undefined';
  const ariaLabel = `Name: ${fullName}, 
                     ${hasRole ? `Role: ${roleString}, ` : ''}
                     ${profile ? `${profile.postsCount} posts, ` : ''}
                     ${profile ? `${profile.votesReceivedCount} votes` : ''}`;
  const toolTipId = `${externalUserId}-tooltip`;

  // Profile may not be in focus while hovered, so escape needs to be checked globally
  useEffect(() => {
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowMini(false);
      }
    };
    document.addEventListener('keydown', onEscape, false);

    return () => {
      document.removeEventListener('keydown', onEscape, false);
    };
  }, []);

  return (
    <span className="rc-ProfileArea" css={profileAreaStyle}>
      <ReactRouterLink
        className="nostyle c-profile-image-wrapper pii-hide"
        id={profileLinkId}
        to={getProfileLink(externalUserId)}
        onMouseEnter={() => {
          setShowMini(true);
        }}
        onFocus={() => setShowMini(true)}
        onMouseLeave={() => setShowMini(false)}
        onBlur={() => setShowMini(false)}
        aria-label={ariaLabel}
      >
        <ProfileImageWithRole courseRole={role} profileImageUrl={profileImageUrl} fullName={fullName} alt={fullName} />
      </ReactRouterLink>

      <span data-js="mini-profile" aria-hidden="true" style={{ position: 'absolute', zIndex: 1 }}>
        <MiniProfileRenderer
          source="profileArea"
          externalId={externalUserId}
          courseRole={role}
          toolTipId={toolTipId}
          showMini={showMini}
          profileImageUrl={profileImageUrl}
          fullName={fullName}
        />
      </span>
    </span>
  );
};

export default _.flowRight(
  connectToStores<{}, { externalUserId: number }>(
    ['CourseStore', 'ClassmatesProfileStore'],
    ({ CourseStore, ClassmatesProfileStore: classmatesProfileStore }, { externalUserId }) => {
      return {
        courseId: CourseStore.getCourseId(),
        profile: _.find(classmatesProfileStore.profiles, { externalId: externalUserId }),
      };
    }
  )
)(ProfileArea);
